<script setup lang="ts">
import TheBottomSheet from '@/components/TheBottomSheet.vue'
import ThePremium from '@/components/ThePremium.vue'
import { T } from '@tolgee/vue'
import type { CustomQuestTaskItem } from '@/types/task'
import { useDailyBuyStars } from '@/composables/game/daily'
import BaseButton from '@/components/base/BaseButton.vue'
import { computed, ref } from 'vue'
import { syncPlayer } from '@/composables/game/player'
import { useEcommerce } from '@/composables/game/ecommerce'
import { useConfetti } from '@/composables/game/confetti'
import { useFortuneWheel } from '@/composables/game/fortune-wheel'
import { SPECIAL_ECOMMERCE_ITEM_ID } from '@/types/shared/player-config'
import { useConfig } from '@/composables/game/config'
import { useToaster } from '@/composables/game/toasters'
import { useHapticFeedback } from '@/composables/game/haptic-feedback'
import TaskModalPrizeBox from '@/components/TaskModalPrizeBox.vue'
import { formatUnshortenedNumber } from '../../helpers/utils'

defineProps<{
	show: boolean
	isLoading: boolean
	task: CustomQuestTaskItem
}>()
const emit = defineEmits<{
	(event: 'hide'): void,
	(event: 'claim'): void
}>()

const { claimedDailyBuyStars, dailyStarsDeposit, maxStarsCount } = useDailyBuyStars()
const { purchase } = useEcommerce()
const { showConfetti } = useConfetti()
const { updateState: updateFortuneWheelState } = useFortuneWheel()
const { config } = useConfig()
const { notify } = useToaster()
const { addHapticFeedback } = useHapticFeedback()

const canClaim = computed(() => {
  return dailyStarsDeposit.value >= maxStarsCount.value
})
const isPurchasing = ref(false)
const ecommerceItem = computed(() => config.value.game.ecommerce.items.find(item => item.id === SPECIAL_ECOMMERCE_ITEM_ID.STAR_100))

async function claim() {
  addHapticFeedback('light')
  if (!claimedDailyBuyStars.value && canClaim.value) {
    emit('claim')
    return
  }
  isPurchasing.value = true
  try {
    if (!ecommerceItem.value) {
      throw new Error()
    }
    const { paid } = await purchase({ type: 'item', item: ecommerceItem.value })
    if (paid) {
      addHapticFeedback('medium')
      showConfetti()
      await syncPlayer()
      await updateFortuneWheelState()
      notify({ type: 'success', title: 'Stars added to Stars Balance' })
    }
  } catch (e) {
    notify({ type: 'error', title: 'Failed to purchase' })
  } finally {
    isPurchasing.value = false
  }
}
</script>

<template>
  <TheBottomSheet
    :show="show"
    @hide="$emit('hide')"
  >
    <div class="flex flex-col gap-y-8">
      <div
        class="w-full flex flex-col items-center gap-y-6"
      >
        <ThePremium
          :with-tooltip="false"
          image-class="size-[120px] min-w-[124px]"
        />
        <div class="flex flex-col items-center">
          <div class="text-light-white text-[24px] leading-8 font-bold -tracking-[0.48px]">
            Deposit {{ formatUnshortenedNumber(maxStarsCount) }} Stars
          </div>
          <div class="text-center text-base font-medium text-white/60">
            <T
              key-name="quests.buy_stars.description"
              default-value="Deposit stars into Dropee daily to unlock bigger and better rewards each day!"
            />
          </div>
        </div>
        <TaskModalPrizeBox
          :reward="task.reward as number"
          :spins="task.spins as number"
          :tickets="task.tickets as number"
        />
        <BaseButton
          class="w-full"
          :loading="isLoading || isPurchasing"
          @click="claim"
        >
          <T
            v-if="claimedDailyBuyStars || !canClaim"
            key-name="quests.buy_stars.button_label"
            default-value="Deposit stars"
          />
          <T
            v-else
            key-name="quests.buy_stars.claim"
            default-value="Claim"
          />
        </BaseButton>
      </div>
    </div>
  </TheBottomSheet>
</template>

<style scoped>

</style>
